import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchBlogPost, fetchBlog, resolveImg } from "../services/api";
import { Button } from "../components/ui/button";
import { CalendarDays, User, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchBlogPost(slug)
      .then((p) => { setPost(p); return fetchBlog(); })
      .then((all) => setOthers(all.filter((x) => x.slug !== slug).slice(0, 3)))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>;

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display font-bold text-2xl">Article not found</h1>
        <Link to="/blog" className="text-[#C9A227] mt-3 inline-block">Back to Blog</Link>
      </div>
    );
  }

  const paragraphs = (post.content || "").split(/\n\n+/).filter(Boolean);

  return (
    <div className="py-10">
      <article className="max-w-3xl mx-auto px-6">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#C9A227] mb-6"><ArrowLeft className="w-4 h-4" /> Back to Blog</Link>
        <span className="text-[#C9A227] font-bold text-xs uppercase tracking-widest">{post.category}</span>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3 leading-tight">{post.title}</h1>
        <div className="flex items-center gap-4 text-sm text-neutral-500 mt-4 mb-8">
          <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {post.date}</span>
          <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author}</span>
        </div>
        <img src={resolveImg(post.image)} alt={post.title} className="w-full rounded-2xl aspect-video object-cover mb-8" />
        <p className="text-lg text-neutral-700 font-medium leading-relaxed mb-6">{post.excerpt}</p>
        {paragraphs.map((p, i) => (<p key={i} className="text-neutral-700 leading-relaxed mb-5">{p}</p>))}

        <div className="mt-10 p-8 rounded-2xl bg-neutral-50 border border-neutral-100 text-center">
          <h3 className="font-display font-bold text-xl text-[#14171c]">Ready to start your project?</h3>
          <p className="text-neutral-600 mt-2">Get a factory-direct quotation within 24 hours.</p>
          <Link to="/contact"><Button className="mt-5 bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-full h-12 px-7">Request a Quote <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>
      </article>

      {others.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 mt-20">
          <h2 className="font-display font-extrabold text-2xl text-[#14171c] mb-8">More Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-neutral-100 card-lift">
                <div className="h-44 overflow-hidden"><img src={resolveImg(p.image)} alt={p.title} className="w-full h-full object-cover img-zoom" /></div>
                <div className="p-5">
                  <span className="text-[#C9A227] font-bold text-xs uppercase tracking-widest">{p.category}</span>
                  <h3 className="font-display font-bold text-[15px] text-[#14171c] mt-2 leading-snug group-hover:text-[#C9A227] transition-colors">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPost;
